import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { submitLead } from "@/utils/leads";
import {
  ArrowRight,
  ArrowLeft,
  Building2,
  Home,
  Users,
  Package,
  HeartPulse,
  MoveUp,
  Layers,
  Sparkles,
  CheckCircle2,
  Phone,
  Loader2,
} from "lucide-react";
import SEO from "@/components/SEO";
import { PageHeader } from "./ServicesPage";
import { BUSINESS } from "@/constants/business";
import { trackLead } from "@/utils/analytics";


// Pricing model (indicative, in INR lakhs). Real quote will follow after site survey.
// base × floor-multiplier × finish-multiplier
const buildingTypes = [
  { id: "villa", label: "Villa / Home", icon: <Home className="w-6 h-6" />, base: 6.5, desc: "Private homes, duplexes" },
  { id: "apartment", label: "Residential Apartment", icon: <Building2 className="w-6 h-6" />, base: 9, desc: "Multi-storey residential" },
  { id: "office", label: "Office / Commercial", icon: <Users className="w-6 h-6" />, base: 11, desc: "Corporate offices, IT parks" },
  { id: "industrial", label: "Factory / Warehouse", icon: <Package className="w-6 h-6" />, base: 12, desc: "Freight & cargo lifts" },
  { id: "hospital", label: "Hospital / Healthcare", icon: <HeartPulse className="w-6 h-6" />, base: 14, desc: "Stretcher-friendly cabins" },
  { id: "hotel", label: "Hotel / Retail", icon: <MoveUp className="w-6 h-6" />, base: 12, desc: "High-traffic public" },
];

const capacities = [
  { id: "small", label: "4 – 6 persons", kg: "272 – 408 kg", mult: 0.85 },
  { id: "medium", label: "8 – 10 persons", kg: "544 – 680 kg", mult: 1.0 },
  { id: "large", label: "13 – 16 persons", kg: "884 – 1088 kg", mult: 1.25 },
  { id: "xlarge", label: "20+ persons / Freight", kg: "1350+ kg", mult: 1.55 },
];

const finishes = [
  { id: "standard", label: "Standard Steel", desc: "Powder-coated MS panels", mult: 1.0 },
  { id: "premium", label: "Premium Stainless", desc: "SS 304 hairline finish", mult: 1.2 },
  { id: "luxury", label: "Luxury Custom", desc: "Wood veneer, mirror, LED", mult: 1.45 },
];

export default function CostCalculatorPage() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    building: null,
    floors: 4,
    capacity: null,
    finish: null,
    name: "",
    phone: "",
    city: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const estimate = useMemo(() => {
    if (!data.building || !data.capacity || !data.finish) return null;
    const b = buildingTypes.find((x) => x.id === data.building);
    const c = capacities.find((x) => x.id === data.capacity);
    const f = finishes.find((x) => x.id === data.finish);
    const floorMultiplier = 1 + Math.max(0, data.floors - 2) * 0.13; // each floor above 2 adds ~13%
    const base = b.base * c.mult * f.mult * floorMultiplier;
    const low = Math.round(base * 0.92 * 10) / 10;
    const high = Math.round(base * 1.18 * 10) / 10;
    return { low, high };
  }, [data]);

  const canProceed = () => {
    if (step === 0) return !!data.building;
    if (step === 1) return data.floors >= 2 && data.floors <= 40;
    if (step === 2) return !!data.capacity;
    if (step === 3) return !!data.finish;
    return true;
  };

  const submit = async () => {
    if (!data.name.trim() || !data.phone.trim()) return;
    setSubmitting(true);
    const b = buildingTypes.find((x) => x.id === data.building);
    const c = capacities.find((x) => x.id === data.capacity);
    const f = finishes.find((x) => x.id === data.finish);
    try {
      await submitLead({
        name: data.name,
        phone: data.phone,
        city: data.city,
        source_page: "cost-calculator",
        service_interest: `Cost Calculator: ${b.label}, ${data.floors} floors, ${c.label}, ${f.label}`,
        message: `Estimated range: ₹${estimate.low}L – ₹${estimate.high}L. Requesting site survey and detailed quote.`,
        whatsapp_optin: true,
      });
      trackLead({
        source: "cost-calculator",
        building: b.label,
        floors: data.floors,
        capacity: c.label,
        finish: f.label,
        estimate_low: estimate.low,
        estimate_high: estimate.high,
      });
      setSubmitted(true);
    } catch (e) {
      alert("Could not submit. Please call us at " + BUSINESS.phone);
    } finally {
      setSubmitting(false);
    }
  };

  const stepTitles = [
    "What are you building for?",
    "How many floors?",
    "How many people per trip?",
    "What cabin finish do you prefer?",
    "Your estimated cost range",
  ];

  return (
    <>
      <SEO
        title="Elevator Cost Calculator | Estimate Your Lift Price | Vila Elevators Bangalore"
        description="Free elevator cost calculator — estimate your passenger elevator, home lift, freight or hospital lift price in India. Get a personalized quote from Vila Elevators."
        path="/blog/elevator-cost-calculator"
        keywords="elevator cost calculator, lift price india, elevator price bangalore, home lift cost, elevator quote calculator, passenger lift cost"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "Vila Elevator Cost Calculator",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          offers: { "@type": "Offer", price: "0", priceCurrency: "INR" },
        }}
      />

      <PageHeader
        eyebrow="Interactive Tool"
        title="Estimate your elevator cost in 4 quick steps."
        desc="Get a data-driven price range instantly, then talk to our team for a precise on-site quote. No email required, no spam."
      />

      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          {/* Progress bar */}
          <div className="flex items-center gap-2 mb-12">
            {[0, 1, 2, 3, 4].map((s) => (
              <div
                key={s}
                data-testid={`calc-progress-${s}`}
                className={`h-1 flex-1 transition-colors ${
                  s <= step ? "bg-[#b08d57]" : "bg-[#f5efe6]"
                }`}
              />
            ))}
          </div>

          <div className="flex items-center justify-between mb-8">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57]">
              Step {step + 1} of 5
            </div>
            <div className="text-[#8a8a8a] text-xs">
              {Math.min(step + 1, 5) * 20}% complete
            </div>
          </div>

          <h2
            data-testid="calc-step-title"
            className="font-display text-3xl sm:text-4xl font-bold text-[#1a1a1a] mb-10"
          >
            {stepTitles[step]}
          </h2>

          <div className="border border-[#e5ddd0] bg-[#faf7f2]/40 p-6 sm:p-10 min-h-[420px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.3 }}
              >
                {step === 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {buildingTypes.map((b) => (
                      <button
                        key={b.id}
                        type="button"
                        data-testid={`calc-building-${b.id}`}
                        onClick={() => setData({ ...data, building: b.id })}
                        className={`text-left p-6 border transition-all ${
                          data.building === b.id
                            ? "border-[#b08d57] bg-[#b08d57]/10"
                            : "border-[#e5ddd0] bg-[#faf7f2] hover:border-[#e5ddd0]"
                        }`}
                      >
                        <div className={`mb-4 ${data.building === b.id ? "text-[#b08d57]" : "text-[#6b6b6b]"}`}>
                          {b.icon}
                        </div>
                        <div className="font-display font-bold text-[#1a1a1a] mb-1">{b.label}</div>
                        <div className="text-xs text-[#8a8a8a]">{b.desc}</div>
                      </button>
                    ))}
                  </div>
                )}

                {step === 1 && (
                  <div className="max-w-2xl">
                    <div className="mb-8">
                      <div className="text-6xl sm:text-7xl font-display font-black text-[#b08d57] mb-2" data-testid="calc-floors-display">
                        {data.floors}
                      </div>
                      <div className="text-[#6b6b6b] text-lg">
                        {data.floors === 2 ? "floors (G+1)" : `floors (G+${data.floors - 1})`}
                      </div>
                    </div>
                    <input
                      type="range"
                      data-testid="calc-floors-range"
                      min="2"
                      max="30"
                      value={data.floors}
                      onChange={(e) => setData({ ...data, floors: Number(e.target.value) })}
                      className="w-full accent-[#b08d57]"
                    />
                    <div className="flex justify-between text-xs text-[#8a8a8a] mt-3">
                      <span>2 floors</span>
                      <span>15 floors</span>
                      <span>30 floors</span>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-8">
                      {[3, 5, 8, 12, 20].map((n) => (
                        <button
                          key={n}
                          type="button"
                          onClick={() => setData({ ...data, floors: n })}
                          className={`px-4 py-2 text-sm border transition-colors ${
                            data.floors === n
                              ? "border-[#b08d57] text-[#b08d57] bg-[#b08d57]/10"
                              : "border-[#e5ddd0] text-[#6b6b6b] hover:border-[#e5ddd0]"
                          }`}
                        >
                          {n} floors
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {capacities.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        data-testid={`calc-capacity-${c.id}`}
                        onClick={() => setData({ ...data, capacity: c.id })}
                        className={`text-left p-6 border transition-all ${
                          data.capacity === c.id
                            ? "border-[#b08d57] bg-[#b08d57]/10"
                            : "border-[#e5ddd0] bg-[#faf7f2] hover:border-[#e5ddd0]"
                        }`}
                      >
                        <div className={`mb-3 ${data.capacity === c.id ? "text-[#b08d57]" : "text-[#6b6b6b]"}`}>
                          <Users className="w-6 h-6" />
                        </div>
                        <div className="font-display font-bold text-[#1a1a1a] text-lg mb-1">{c.label}</div>
                        <div className="text-sm text-[#b08d57]">{c.kg}</div>
                      </button>
                    ))}
                  </div>
                )}

                {step === 3 && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {finishes.map((f) => (
                      <button
                        key={f.id}
                        type="button"
                        data-testid={`calc-finish-${f.id}`}
                        onClick={() => setData({ ...data, finish: f.id })}
                        className={`text-left p-6 border transition-all ${
                          data.finish === f.id
                            ? "border-[#b08d57] bg-[#b08d57]/10"
                            : "border-[#e5ddd0] bg-[#faf7f2] hover:border-[#e5ddd0]"
                        }`}
                      >
                        <div className={`mb-3 ${data.finish === f.id ? "text-[#b08d57]" : "text-[#6b6b6b]"}`}>
                          <Sparkles className="w-6 h-6" />
                        </div>
                        <div className="font-display font-bold text-[#1a1a1a] mb-1">{f.label}</div>
                        <div className="text-xs text-[#8a8a8a]">{f.desc}</div>
                      </button>
                    ))}
                  </div>
                )}

                {step === 4 && estimate && !submitted && (
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57] mb-3">
                      Estimated Range
                    </div>
                    <div className="font-display text-5xl sm:text-6xl font-black text-[#1a1a1a] mb-3" data-testid="calc-estimate">
                      ₹{estimate.low}L
                      <span className="text-[#8a8a8a] mx-3 text-4xl">–</span>
                      ₹{estimate.high}L
                    </div>
                    <p className="text-[#6b6b6b] text-sm leading-relaxed mb-8 max-w-lg">
                      Indicative range for a {buildingTypes.find((x) => x.id === data.building).label.toLowerCase()} elevator, {data.floors} floors, {capacities.find((x) => x.id === data.capacity).label}, {finishes.find((x) => x.id === data.finish).label.toLowerCase()} finish. Final price depends on shaft, site conditions, drive type and accessories.
                    </p>

                    <div className="border-t border-[#e5ddd0] pt-8">
                      <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57] mb-2">
                        Get Your Precise Quote
                      </div>
                      <h3 className="font-display text-2xl font-bold text-[#1a1a1a] mb-6">
                        Share your details for a free site survey.
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <input
                          data-testid="calc-name"
                          placeholder="Your name *"
                          value={data.name}
                          onChange={(e) => setData({ ...data, name: e.target.value })}
                          className="bg-[#faf7f2] border border-[#e5ddd0] focus:border-[#b08d57] outline-none px-4 py-3 text-[#1a1a1a] text-sm"
                        />
                        <input
                          data-testid="calc-phone"
                          placeholder="Phone number *"
                          type="tel"
                          value={data.phone}
                          onChange={(e) => setData({ ...data, phone: e.target.value })}
                          className="bg-[#faf7f2] border border-[#e5ddd0] focus:border-[#b08d57] outline-none px-4 py-3 text-[#1a1a1a] text-sm"
                        />
                        <input
                          data-testid="calc-city"
                          placeholder="City (Bangalore, Chennai, etc.)"
                          value={data.city}
                          onChange={(e) => setData({ ...data, city: e.target.value })}
                          className="bg-[#faf7f2] border border-[#e5ddd0] focus:border-[#b08d57] outline-none px-4 py-3 text-[#1a1a1a] text-sm sm:col-span-2"
                        />
                      </div>
                      <button
                        type="button"
                        data-testid="calc-submit"
                        onClick={submit}
                        disabled={submitting || !data.name.trim() || !data.phone.trim()}
                        className="mt-6 w-full sm:w-auto bg-[#b08d57] hover:bg-[#8a6a3d] disabled:opacity-50 text-white px-8 py-4 font-semibold uppercase tracking-wider text-sm transition-colors inline-flex items-center gap-3"
                      >
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                        {submitting ? "Sending…" : "Request Site Survey"}
                      </button>
                      <p className="text-xs text-[#8a8a8a] mt-4">
                        Or call us directly: <a href={`tel:${BUSINESS.phoneRaw}`} className="text-[#b08d57] font-semibold">{BUSINESS.phone}</a>
                      </p>
                    </div>
                  </div>
                )}

                {step === 4 && submitted && (
                  <div className="text-center py-8" data-testid="calc-success">
                    <CheckCircle2 className="w-16 h-16 text-[#b08d57] mx-auto mb-6" />
                    <h3 className="font-display text-3xl font-bold text-[#1a1a1a] mb-3">
                      Thank you, {data.name.split(" ")[0]}!
                    </h3>
                    <p className="text-[#6b6b6b] max-w-md mx-auto mb-8">
                      Our team will call you within one business day to schedule a free site survey. Your estimated range was <span className="text-[#b08d57] font-semibold">₹{estimate.low}L – ₹{estimate.high}L</span>.
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                      <a
                        href={`tel:${BUSINESS.phoneRaw}`}
                        className="inline-flex items-center gap-2 bg-[#b08d57] hover:bg-[#8a6a3d] text-white px-6 py-3 font-semibold uppercase tracking-wider text-sm transition-colors"
                      >
                        <Phone className="w-4 h-4" /> Call {BUSINESS.phone}
                      </a>
                      <Link
                        to="/blog"
                        className="inline-flex items-center gap-2 text-[#6b6b6b] hover:text-[#1a1a1a] text-sm"
                      >
                        Read our blog <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation */}
          {!submitted && step < 4 && (
            <div className="flex items-center justify-between mt-8">
              <button
                type="button"
                data-testid="calc-prev"
                onClick={() => setStep(Math.max(0, step - 1))}
                disabled={step === 0}
                className="inline-flex items-center gap-2 text-[#6b6b6b] hover:text-[#1a1a1a] disabled:opacity-30 disabled:hover:text-[#6b6b6b] text-sm font-semibold uppercase tracking-wider transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> Previous
              </button>
              <button
                type="button"
                data-testid="calc-next"
                onClick={() => setStep(step + 1)}
                disabled={!canProceed()}
                className="inline-flex items-center gap-2 bg-[#b08d57] hover:bg-[#8a6a3d] disabled:opacity-30 text-white px-8 py-4 font-semibold uppercase tracking-wider text-sm transition-colors"
              >
                {step === 3 ? "See Estimate" : "Next"} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Disclaimer */}
          <div className="mt-8 border border-[#e5ddd0] bg-[#faf7f2] p-5 flex items-start gap-3">
            <Layers className="w-4 h-4 text-[#b08d57] flex-shrink-0 mt-0.5" />
            <p className="text-xs text-[#8a8a8a] leading-relaxed">
              <strong className="text-[#4a4a4a]">How this works:</strong> Our calculator uses real Bangalore-market pricing baselines and applies multipliers for capacity, floors and finish. It's meant to give you a directional range — the final quote depends on site inspection, drive type (MRL / traction / hydraulic), regulatory approvals and cabin customisation.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
